// Inställningar: who is logged in (and the way out), push reminders with the
// iOS standalone install gate, and text size. Three cards, nothing more — the
// settings screen has to be readable at a glance.

import { getPushKey, postPushSubscribe } from "../api";
import { getSession, logout } from "../auth";
import { getTextSize, setTextSize, type TextSize } from "../config";
import { clear, el } from "../dom";
import { t, type StringKey } from "../strings";

function isIOS(): boolean {
  const ua = navigator.userAgent;
  const classic = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ masquerades as macOS but is touch-first.
  const ipadOs = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return classic || ipadOs;
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const SIZES: Array<{ value: TextSize; key: StringKey }> = [
  { value: "normal", key: "settings.textsize.normal" },
  { value: "stor", key: "settings.textsize.large" },
  { value: "storst", key: "settings.textsize.largest" },
];

export function mountInstallningar(root: HTMLElement): () => void {
  let disposed = false;
  clear(root);
  root.append(el("h1", { text: t("settings.title") }));

  // ── account ──────────────────────────────────────────────────────────────
  const accountCard = el("div", { class: "card" });
  accountCard.append(el("h2", { text: t("settings.account.title") }));
  const session = getSession();
  accountCard.append(
    el("p", {
      text: session
        ? `${t("settings.account.signedInAs")} ${session.email}`
        : t("settings.account.signedOut"),
    })
  );
  const logoutBtn = el("button", {
    class: "btn",
    type: "button",
    text: t("settings.account.logout"),
  });
  logoutBtn.addEventListener("click", () => {
    logout();
    location.hash = "#/valkommen";
  });
  accountCard.append(logoutBtn);
  root.append(accountCard);

  // ── push ─────────────────────────────────────────────────────────────────
  const pushCard = el("div", { class: "card" });
  pushCard.append(el("h2", { text: t("settings.push.title") }));
  pushCard.append(el("p", { class: "muted small", text: t("settings.push.hint") }));
  const pushStatus = el("div", { class: "voice-state", text: "" });

  if (isIOS() && !isStandalone()) {
    // iOS Safari only allows Web Push from an installed (standalone) PWA.
    const gate = el("div", { class: "notice" });
    gate.append(el("strong", { text: t("settings.ios.title") }), el("br"));
    gate.append(el("span", { text: t("settings.ios.body") }));
    pushCard.append(gate);
  } else if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    pushCard.append(el("div", { class: "notice", text: t("settings.push.unsupported") }));
  } else {
    const enableBtn = el("button", { class: "btn primary", text: t("settings.push.enable") });
    enableBtn.addEventListener("click", () => void enablePush(enableBtn, pushStatus));
    pushCard.append(enableBtn, pushStatus);
    // Reflect an existing subscription so the button is honest on revisit.
    void navigator.serviceWorker.getRegistration().then(async (reg) => {
      const sub = await reg?.pushManager.getSubscription();
      if (sub && !disposed) {
        enableBtn.disabled = true;
        pushStatus.textContent = t("settings.push.enabled");
      }
    });
  }
  root.append(pushCard);

  async function enablePush(btn: HTMLButtonElement, status: HTMLElement): Promise<void> {
    btn.disabled = true;
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        status.textContent = t("settings.push.denied");
        btn.disabled = false;
        return;
      }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { key } = await getPushKey();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
      });
      const json = sub.toJSON();
      await postPushSubscribe({
        endpoint: sub.endpoint,
        p256dh: json.keys?.["p256dh"] ?? "",
        auth: json.keys?.["auth"] ?? "",
        ua: navigator.userAgent,
        role: "patient",
      });
      status.textContent = t("settings.push.enabled");
    } catch {
      status.textContent = t("settings.push.error");
      btn.disabled = false;
    }
  }

  // ── text size ────────────────────────────────────────────────────────────
  // Each button is set in the size it picks, so the choice is visible before
  // it is made.
  const sizeCard = el("div", { class: "card" });
  sizeCard.append(el("h2", { text: t("settings.textsize.title") }));
  sizeCard.append(el("p", { class: "muted small", text: t("settings.textsize.hint") }));
  const sizeRow = el("div", { class: "textsize-row" });
  const sizeButtons = SIZES.map((size) => {
    const btn = el("button", { class: "btn", type: "button", text: t(size.key) });
    btn.addEventListener("click", () => {
      setTextSize(size.value);
      markSelected();
    });
    sizeRow.append(btn);
    return { value: size.value, btn };
  });

  function markSelected(): void {
    const current = getTextSize();
    for (const { value, btn } of sizeButtons) {
      btn.classList.toggle("selected", value === current);
      btn.setAttribute("aria-pressed", value === current ? "true" : "false");
    }
  }

  markSelected();
  sizeCard.append(sizeRow);
  root.append(sizeCard);

  return () => {
    disposed = true;
  };
}
